import type { DataChartStyleProps } from './datachartStyleRegistry'
import { useEnergyDeviceSnapshot } from './useEnergyDeviceSnapshot'
import './EnergyCard.css'

type PhaseItem = {
  id: 'r' | 's' | 't'
  label: string
  color: string
  power: number | null
  voltage: number | null
  current: number | null
  arcLength: number
  arcOffset: number
}

const SEMI_ARC_PATH = 'M10 50 A40 40 0 0 1 90 50'

function formatInt(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '--'
  return value.toLocaleString('en-US')
}

function formatFixed(value: number | null, digits: number): string {
  if (value === null || !Number.isFinite(value)) return '--'
  return value.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

function Ed2Phase({ phase }: { phase: PhaseItem }) {
  return (
    <div className="energy-card-ed2__phase">
      <div className="energy-card-ed2__gauge" style={{ color: phase.color }}>
        <svg className="energy-card-ed2__gauge-svg" viewBox="0 0 100 60" aria-hidden>
          <path className="energy-card-ed2__gauge-bg" d={SEMI_ARC_PATH} pathLength={100}></path>
          <path
            className="energy-card-ed2__gauge-arc"
            d={SEMI_ARC_PATH}
            pathLength={100}
            style={{ stroke: phase.color, strokeDasharray: `${phase.arcLength} 100`, strokeDashoffset: `${phase.arcOffset}` }}
          ></path>
        </svg>
      </div>
      <span className="energy-card-ed2__badge" style={{ color: phase.color, borderColor: `${phase.color}33`, background: `${phase.color}1a` }}>
        {phase.label}
      </span>
      <div className="energy-card-ed2__power">{formatInt(phase.power)}W</div>
      <div className="energy-card-ed2__meta">
        {formatFixed(phase.voltage, 1)}V | {formatFixed(phase.current, 1)}A
      </div>
    </div>
  )
}

export function EnergyCardStyle03(props: DataChartStyleProps) {
  const { snapshot } = useEnergyDeviceSnapshot(props.deviceSN, props.credentials)
  const phaseItems: PhaseItem[] = [
    {
      id: 'r',
      label: 'R相',
      color: '#ff4d4d',
      power: snapshot?.phases.r.power ?? null,
      voltage: snapshot?.phases.r.voltage ?? null,
      current: snapshot?.phases.r.current ?? null,
      arcLength: 34,
      arcOffset: -50,
    },
    {
      id: 's',
      label: 'S相',
      color: '#ffb300',
      power: snapshot?.phases.s.power ?? null,
      voltage: snapshot?.phases.s.voltage ?? null,
      current: snapshot?.phases.s.current ?? null,
      arcLength: 34,
      arcOffset: -28,
    },
    {
      id: 't',
      label: 'T相',
      color: '#3b82f6',
      power: snapshot?.phases.t.power ?? null,
      voltage: snapshot?.phases.t.voltage ?? null,
      current: snapshot?.phases.t.current ?? null,
      arcLength: 34,
      arcOffset: -42,
    },
  ]

  return (
    <div className="energy-card energy-card--ed2" data-css-slot="cardFrame" data-css-slot-label="Card Frame">
      <div className="energy-card-ed2__live">
        <span>LIVE</span>
        <span className="energy-card-ed2__live-dot" />
      </div>

      <div className="energy-card-ed2__title">Power Consumption</div>
      <div className="energy-card-ed2__total">
        Total: {formatInt(snapshot?.totalPower ?? null)}
        <small>W</small>
      </div>

      <div className="energy-card-ed2__grid">
        {phaseItems.map((phase) => (
          <Ed2Phase key={phase.id} phase={phase} />
        ))}
      </div>
    </div>
  )
}
